import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { EApiPath } from '../../enums';
import { InvoiceSettings, Lookups } from '../models/models';

/**
 * The exact field list `Shekhawat Update Invoice Settings` accepts — its
 * `reqBodySchema` keys, which match the handler's own UPDATABLE_FIELDS. Anything
 * outside this list (`_id`, `updatedAt`, ...) is rejected by the platform with a
 * 400 before the handler runs, so it must never be sent.
 */
const UPDATABLE_SETTINGS_FIELDS = [
  'mobile_no',
  'firm_name',
  'email',
  'address',
  'state',
  'next_invoice_no',
  'gst_number',
  'gst_scheme',
  'bank',
  'ifsc',
  'account_no',
] as const satisfies readonly (keyof InvoiceSettings)[];

/**
 * Copies across only the whitelisted fields that are actually present. Keys the
 * caller never set are left off entirely rather than sent as `undefined` — the
 * handler treats a present-but-undefined field as "no change" too, but omitting
 * them keeps the request body honest about what is being changed.
 */
function pickUpdatableSettings(payload: InvoiceSettings): Partial<InvoiceSettings> {
  const body: Partial<InvoiceSettings> = {};
  for (const field of UPDATABLE_SETTINGS_FIELDS) {
    const value = payload[field];
    if (value !== undefined) (body as Record<string, unknown>)[field] = value;
  }
  return body;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private api = inject(ApiService);

  /** Singleton seller profile — the backend creates a default row if none exists. */
  getInvoiceSettings(): Observable<InvoiceSettings> {
    return this.api.callCustomApi<InvoiceSettings>(EApiPath.INVOICE_SETTINGS, 'GET');
  }

  updateInvoiceSettings(payload: InvoiceSettings): Observable<InvoiceSettings> {
    // Send ONLY the fields update-invoice-settings declares in its reqBodySchema.
    // The GET returns the raw DB row — including `_id` and `updatedAt` — and the
    // settings screen edits that object in place, so posting it back verbatim sent
    // two fields the schema does not declare. api_maker rejects any undeclared key
    // outright ("Property not found in schema for key '_id'."), so every save 400'd.
    // Whitelisting here rather than in the component keeps any future caller safe.
    // The backend ignores `_id` regardless: it resolves the singleton row itself and
    // copies only its own UPDATABLE_FIELDS list off the body.
    return this.api.callCustomApi<InvoiceSettings>(
      EApiPath.UPDATE_INVOICE_SETTINGS,
      'POST',
      pickUpdatableSettings(payload),
    );
  }

  /** Static enums (states, banks, job statuses, ...) served from one backend source. */
  getLookups(): Observable<Lookups> {
    return this.api.callCustomApi<Lookups>(EApiPath.LOOKUPS, 'GET');
  }
}
